import React from 'react';
import { useCurrentFrame } from 'remotion';
import { Adjustments, Layer, ProjectState } from "../../shared";

type Props = {
    project: ProjectState,
    layer: Layer,
    children: React.ReactNode,
};

export default function DetectedObjects({ project, layer, children }: Props) {
    const frame = useCurrentFrame();

    if (!layer.detectedObjects) return (
        <>{children}</>
    );

    const { 
        blendMode, 
        opacity,
        visible,
    } = layer;

    const { x, y } = layer.position;
    let { width, height } = layer.dimensions;
    const currentFrameObjects = layer.detectedObjects.filter((o) => {
        if (layer.type === "video") {
            return o.frame === frame;
        }
        return true;
    });
    
    const polygonsByName: {[key: string]: JSX.Element[]} = {};

    currentFrameObjects?.forEach(({ name, points }, index) => {
        let pointString = "";
        let minX = width;
        let minY = height;
        let maxX = 0; 
        let maxY = 0;
        points.forEach((point) => {
            const x = point.x * width;
            const y = point.y * height;
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            pointString += `${x},${y} `;
        });
        const polygon = (
            <polygon
                key={index}
                points={pointString}
                style={{
                    fill: 'transparent',
                    // stroke: 'white',
                    // strokeWidth: 1,
                }}
            />
        )
        if (!polygonsByName[name]) {
            polygonsByName[name] = [];
        }
        polygonsByName[name].push(polygon)
    });

    // const backgroundStyles: React.CSSProperties = {
    //     filter: `
    //         brightness(1) 
    //         contrast(1) 
    //         saturate(1) 
    //         hue-rotate(0deg)
    //         grayscale(1)`,
    // };

    // styles for each detected object class
    const objectStylesByName: {[key: string]: React.CSSProperties} = {};
    if (project.objectAdjustments) {
        Object.keys(project.objectAdjustments).forEach(name => {
            const adjustments: Adjustments = project.objectAdjustments?.[name] || {};
            let filter = ``;
            if (adjustments.brightnessContrast) {
                const {
                    brightness,
                    contrast
                } = adjustments.brightnessContrast;
                filter = `${filter} brightness(${brightness + 1})`;
                filter = `${filter} contrast(${contrast + 1})`;
            }
            if (adjustments.hueSaturation) {
                const {
                    hue,
                    saturation
                } = adjustments.hueSaturation;
                filter = `${filter} hue-rotate(${hue * 180}deg)`;
                filter = `${filter} saturate(${saturation + 1})`;
            }
            if (adjustments.lensBlur) {
                const {
                    radius
                } = adjustments.lensBlur;
                filter = `${filter} blur(${radius}px)`;
            }
            console.log('filter', filter);
            objectStylesByName[name] = {
                filter
            };
        })
    }

    return (
        <div style={{
            opacity: visible ? opacity : 0,
            mixBlendMode: blendMode,
        }}>
            <div style={objectStylesByName['background']}>
                {children}
            </div>
            <svg style={{
                position: 'absolute',
                top: y || 0,
                left: x || 0,
                width,
                height,
            }}>
                {Object.keys(polygonsByName).map(name => (
                    <clipPath
                        key={name}
                        id={`clipPathPolygons-${name.replace(" ", "_")}`}>
                        {polygonsByName[name]}
                    </clipPath>
                ))}
                {Object.keys(polygonsByName).map(name => (
                    <React.Fragment
                        key={name}>
                        {polygonsByName[name]}
                    </React.Fragment>
                ))}
            </svg>
            {Object.keys(polygonsByName).map(name => (
                <div 
                    key={name}
                    style={{ 
                        ...objectStylesByName[name],
                        clipPath: `url(#clipPathPolygons-${name.replace(" ", "_")})`, 
                        position: 'absolute',
                        top: y || 0,
                        left: x || 0,
                        width,
                        height,
                    }}>
                    {children}
                </div>
            ))} 
        </div>
    )
}